// components/create/CreateDrawer.tsx

"use client";

import React, { useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";

import { useAppSelector } from "../../redux/hooks";
import { selectActiveProfileType } from "../../redux/selectors/profile/userSelectors";

import { getCreateItems } from "./createItems";
import "./CreateDrawer.css";

interface CreateDrawerProps {
  open: boolean;
  onClose: () => void;
}

const CreateDrawer: React.FC<CreateDrawerProps> = ({ open, onClose }) => {
  const router = useRouter();
  const activeProfileType = useAppSelector(selectActiveProfileType);

  const createItems = useMemo(
    () => getCreateItems(activeProfileType),
    [activeProfileType]
  );

  // Close on ESC
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        className="create-drawer-backdrop"
        aria-label="Close create drawer"
        onClick={onClose}
      />

      <aside className="create-drawer" aria-label="Create new">
        <div className="create-drawer-list">
          {createItems.map((item) => {
            const Icon = item.icon;

            if (!item.enabled) {
              return (
                <div
                  key={item.key}
                  className="create-drawer-item is-disabled"
                  title={item.disabledTitle || "Coming soon"}
                  aria-disabled="true"
                >
                  <span className="create-drawer-item-label">{item.label}</span>
                  <span className="create-drawer-item-icon">
                    <Icon size={22} />
                  </span>
                </div>
              );
            }

            return (
              <button
                key={item.key}
                type="button"
                className="create-drawer-item"
                onClick={() => {
                  onClose();
                  if (item.href) router.push(item.href);
                }}
                aria-label={`Create ${item.label}`}
              >
                <span className="create-drawer-item-label">{item.label}</span>
                <span className="create-drawer-item-icon">
                  <Icon size={22} />
                </span>
              </button>
            );
          })}
        </div>
      </aside>
    </>
  );
};

export default CreateDrawer;